/**
 * `<RulesText>` — the shared renderer for **lancer-data rules prose**: the third-party rules text
 * Massif authored and our compendium ingest ships.
 *
 * ## Its input language, and what is NOT its input
 *
 * **It parses the lancer-data HTML subset. It does not parse user-authored text.** That distinction
 * is the reason for the name, not a technicality — the two are different grammars with different
 * trust properties, and feeding one to the other's parser produces silently wrong output rather than
 * an error.
 *
 * **In:** `@massif/lancer-data` (and the packs built on its shape) — HTML fragments carrying **zero
 * newlines**. A paragraph boundary is a literal `<br>`; the corpus census across `lib/` is 437
 * `<br>`, 191 `<li>`, 76 `<b>`, 66 `<ul>`, 53 `<i>`, 8 `<p>`, plus `<span class='horus--subtle'>`
 * and `<code class='horus'>`. Talent ranks alone carry 36 `<br>`, 22 `<ul>` and 51 `<b>`. It also
 * tolerates that corpus's real malformations (an unclosed `<ul>`, a `<br>` inside a `<li>`, a
 * truncated trailing tag) — see the regex comments below, each names the entry that forced it.
 *
 * **Not in:** anything a *user* wrote. Markdown, or plain text with real `\n` paragraph breaks, is a
 * different language: this parser splits on `<br>` and would render `\n`-delimited prose as one
 * undifferentiated block, and would read a user's literal `<` as markup. The right shape for that is
 * a **sibling block-builder feeding this same renderer** — not a widened parser straddling two
 * grammars. `UserText` owns that job; there is deliberately no alias from it to here, precisely so
 * an import can't quietly land on the wrong parser.
 *
 * ## Why it exists
 *
 * Without it a surface renders one of two wrong things:
 *   - fed to React as a **text child**, `<br>` either collapses to nothing visible (after a
 *     `stripTags` pass turns it into a bare `\n` under `white-space: pre-wrap`, indistinguishable
 *     from a wrap at a narrow measure) or shows the literal characters `<br>`;
 *   - injected with `dangerouslySetInnerHTML`, a `<br>` renders exactly like a wrapped line — no
 *     paragraph gap, one undifferentiated wall.
 *
 * ## Why a parser and not `dangerouslySetInnerHTML`
 *
 * The COMPENDIUM record sheet ({@link ../routes/compendium/CompendiumDetail}) injects raw HTML and
 * documents why it's safe *there* (bundled static data). But the compendium **ingests third-party
 * LCP content** (`packages/compendium/src/lcp-zip.ts`), so "the corpus is trusted" is a property of
 * today's data, not of the code path. **This component takes no `dangerouslySetInnerHTML`, and a
 * future `UserText` must not either** — user-authored prose shown to other players is a *more*
 * exposed position than compendium prose, not less. This component therefore never injects markup: it tokenizes
 * the fragment into a small block/inline tree and hands React ordinary elements and text nodes, which
 * React escapes. Tags are consumed during tokenization and entities are decoded **after**, on text
 * nodes only — so a decoded `&lt;script&gt;` can never be re-read as a tag. There is no path from
 * source bytes to markup.
 *
 * ## The block model
 *
 * Boundary detection reuses {@link splitProseParagraphs}, the same seam the COMPENDIUM sheet splits
 * on, so the two renderers can never disagree about where a paragraph starts. Unknown inline tags are
 * **transparent** — the tag is dropped, its text kept — which is what keeps a corpus addition from
 * silently deleting rules text.
 *
 * `{VAL}` placeholders are deliberately NOT substituted here: they only ever appear in *tag
 * definitions*, which no `<RulesText>` call site renders (see `../routes/compendium/valSlot`, which
 * owns that seam for the COMPENDIUM's own surfaces).
 */
import type { ReactNode } from "react";
import { splitProseParagraphs } from "./prose";
import "./rulestext.css";

/**
 * An inline run inside one block. `text` is already entity-decoded and carries no markup.
 *
 * `break` exists for one real corpus shape: a `<br>` **inside** a `<li>`. `t_infiltrator` III closes
 * its bullet list by tucking the rank's closing sentence into the last `<li>` behind a `<br>` — so a
 * list item is not always a single run, and dropping that `<br>` would run two sentences together.
 * Block-level `<br>`s never reach here; the paragraph splitter has already consumed them.
 */
export type RulesInline =
  | { kind: "text"; text: string }
  | { kind: "break" }
  | { kind: "strong"; kids: RulesInline[] }
  | { kind: "em"; kids: RulesInline[] }
  | { kind: "code"; kids: RulesInline[] };

/** One block of prose: a paragraph, or a bulleted list of items. */
export type RulesBlock = { kind: "p"; kids: RulesInline[] } | { kind: "ul"; items: RulesInline[][] };

/** The inline tags the corpus uses, mapped to the node kind each produces. Everything else is transparent. */
const INLINE_KINDS: Record<string, RulesInline["kind"] & ("strong" | "em" | "code")> = {
  b: "strong",
  strong: "strong",
  i: "em",
  em: "em",
  code: "code",
};

/**
 * A `<ul>`/`<ol>` region, captured whole so its `<li>`s can be lifted out before paragraph splitting.
 *
 * The closing tag is optional (`|$`) because the corpus really does ship unclosed lists — `t_technophile`
 * III opens a `<ul>` and simply stops, mid-`<li>`. Requiring `</ul>` makes the whole rank fall through
 * to the prose path, where the `<li>`s are dropped as transparent tags and three bullets run together
 * into one sentence.
 */
const LIST_BLOCK = /<(ul|ol)\b[^>]*>([\s\S]*?)(?:<\/\1\s*>|$)/gi;

/** One `<li>`; the closing tag is optional because the corpus omits it in a few entries. */
const LIST_ITEM = /<li\b[^>]*>([\s\S]*?)(?=<li\b|<\/li\s*>|$)/gi;

/** Any tag, open or close — the only thing the inline tokenizer needs to recognize. */
const ANY_TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;

/** `<p>` boundaries mean the same thing as `<br>` here, so they're normalized into one delimiter. */
const P_TAG = /<\/?p\b[^>]*>/gi;

/**
 * A tag opened at the very end of the fragment and never terminated — markup debris, not rules text.
 * The corpus ships it (`t_nuclear_cavalier` II ends in the literal four characters `</ul`), and with
 * no `>` the tokenizer can't recognize it, so it would otherwise be printed on screen verbatim.
 */
const TRAILING_BROKEN_TAG = /<\/?[a-zA-Z][^>]*$/;

/** The named entities the corpus actually contains, plus the numeric forms. */
const NAMED_ENTITIES: Record<string, string> = {
  nbsp: "\u00a0",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  rsquo: "’",
  lsquo: "‘",
  ldquo: "“",
  rdquo: "”",
  mdash: "—",
  ndash: "–",
  hellip: "…",
};

/**
 * Decode HTML entities in a **text node**. Called only after tags have been consumed by the
 * tokenizer, which is what makes it safe: nothing it produces is ever re-scanned for markup.
 */
export function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, body: string) => {
    if (body.startsWith("#x") || body.startsWith("#X")) {
      const code = Number.parseInt(body.slice(2), 16);
      return Number.isNaN(code) ? whole : String.fromCodePoint(code);
    }
    if (body.startsWith("#")) {
      const code = Number.parseInt(body.slice(1), 10);
      return Number.isNaN(code) ? whole : String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
  });
}

/**
 * Tokenize one block's HTML into inline nodes.
 *
 * A stack of open emphasis containers; an unrecognized tag is transparent (dropped, contents kept)
 * and a stray close tag with no matching open is ignored, so malformed corpus markup degrades to
 * readable text rather than losing a clause.
 */
export function parseInline(html: string): RulesInline[] {
  const root: RulesInline[] = [];
  const stack: { tag: string; kids: RulesInline[] }[] = [];
  const top = () => (stack.length > 0 ? stack[stack.length - 1]!.kids : root);

  const pushText = (raw: string) => {
    if (raw === "") return;
    const text = decodeEntities(raw);
    const into = top();
    const last = into[into.length - 1];
    // Merge adjacent text (a transparent tag splits one run in two) so callers see one node.
    if (last && last.kind === "text") last.text += text;
    else into.push({ kind: "text", text });
  };

  let cursor = 0;
  ANY_TAG.lastIndex = 0;
  for (let m = ANY_TAG.exec(html); m !== null; m = ANY_TAG.exec(html)) {
    pushText(html.slice(cursor, m.index));
    cursor = m.index + m[0].length;
    const closing = m[1] === "/";
    const tag = m[2]!.toLowerCase();
    if (tag === "br") {
      // Only reachable inside a `<li>` — the prose path splits blocks on `<br>` before tokenizing.
      if (!closing) top().push({ kind: "break" });
      continue;
    }
    const kind = INLINE_KINDS[tag];
    if (kind === undefined) continue; // transparent: span, anything new upstream adds
    if (!closing) {
      stack.push({ tag, kids: [] });
      continue;
    }
    // Close the nearest matching open container; unwind anything left dangling inside it.
    const at = stack.map((f) => f.tag).lastIndexOf(tag);
    if (at === -1) continue;
    while (stack.length > at) {
      const frame = stack.pop()!;
      const node = { kind: INLINE_KINDS[frame.tag]!, kids: frame.kids } as RulesInline;
      top().push(node);
    }
  }
  pushText(html.slice(cursor));
  // Anything still open at end-of-string closes here rather than dropping its contents.
  while (stack.length > 0) {
    const frame = stack.pop()!;
    top().push({ kind: INLINE_KINDS[frame.tag]!, kids: frame.kids } as RulesInline);
  }
  return root;
}

/** True when a parsed run holds no visible characters — the guard that drops empty blocks. */
function isBlank(nodes: RulesInline[]): boolean {
  return nodes.every((n) => {
    if (n.kind === "text") return n.text.trim() === "";
    if (n.kind === "break") return true;
    return isBlank(n.kids);
  });
}

/**
 * Parse a compendium HTML fragment into its blocks.
 *
 * Lists are lifted out first (a `<ul>` is a block, and its inner `<li>`s must not be fed to the
 * paragraph splitter); everything between them is `<p>`-normalized and split on `<br>` runs.
 */
export function parseRulesText(source: string): RulesBlock[] {
  const html = source.replace(TRAILING_BROKEN_TAG, "");
  const blocks: RulesBlock[] = [];

  const pushProse = (chunk: string) => {
    for (const para of splitProseParagraphs(chunk.replace(P_TAG, "<br>"))) {
      const kids = parseInline(para);
      if (!isBlank(kids)) blocks.push({ kind: "p", kids });
    }
  };

  let cursor = 0;
  LIST_BLOCK.lastIndex = 0;
  for (let m = LIST_BLOCK.exec(html); m !== null; m = LIST_BLOCK.exec(html)) {
    pushProse(html.slice(cursor, m.index));
    cursor = m.index + m[0].length;
    const items: RulesInline[][] = [];
    LIST_ITEM.lastIndex = 0;
    for (let li = LIST_ITEM.exec(m[2]!); li !== null; li = LIST_ITEM.exec(m[2]!)) {
      const kids = parseInline(li[1]!);
      if (!isBlank(kids)) items.push(kids);
    }
    if (items.length > 0) blocks.push({ kind: "ul", items });
  }
  pushProse(html.slice(cursor));

  return blocks;
}

function inlineText(nodes: RulesInline[]): string {
  return nodes.map((n) => (n.kind === "text" ? n.text : n.kind === "break" ? " " : inlineText(n.kids))).join("");
}

/**
 * A compendium prose fragment as plain text, for an attribute that cannot carry markup (an
 * `aria-description`, a `title`): the same parse {@link RulesText} renders, blocks joined by a space.
 */
export function rulesPlainText(source: string): string {
  return parseRulesText(source)
    .map((b) => (b.kind === "p" ? inlineText(b.kids) : b.items.map(inlineText).join("; ")))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function renderInline(nodes: RulesInline[]): ReactNode {
  return nodes.map((n, i) => {
    if (n.kind === "text") return n.text;
    if (n.kind === "break") return <br key={i} />;
    if (n.kind === "strong") return <strong key={i}>{renderInline(n.kids)}</strong>;
    if (n.kind === "em") return <em key={i}>{renderInline(n.kids)}</em>;
    return (
      <code key={i} className="rulestext-code">
        {renderInline(n.kids)}
      </code>
    );
  });
}

/**
 * Render a compendium prose fragment as real block elements.
 *
 * `className` is appended to `.rulestext`, which supplies only the block rhythm (paragraph gap, list
 * indent) and inherits font/size/color from the host — so the same component reads correctly inside a
 * portalled brutalist tooltip, an editor reader pane, and anywhere else without a per-surface variant.
 * A fragment that parses to nothing renders nothing, so callers can pass a possibly-empty string.
 */
export function RulesText({ html, className }: { html: string; className?: string }) {
  const blocks = parseRulesText(html);
  if (blocks.length === 0) return null;
  return (
    <div className={className ? `rulestext ${className}` : "rulestext"}>
      {blocks.map((b, i) =>
        b.kind === "p" ? (
          <p key={i} className="rulestext-p">
            {renderInline(b.kids)}
          </p>
        ) : (
          <ul key={i} className="rulestext-ul">
            {b.items.map((item, j) => (
              <li key={j}>{renderInline(item)}</li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}
